/**
 * TitleCards — Phase 8 Library Edition (continuation pass).
 *
 * Full-screen serif title cards that fade in/out at three navigation
 * transitions, PLUS a persistent phase banner across the top of the screen
 * that continuously communicates "where you are" throughout the journey,
 * plus the pre-existing subtle bottom-left location label.
 *
 * The continuation pass extends the original Phase 8 implementation in two
 * ways Tom called out from the live site:
 *
 *   1. Hold cards longer (HOLD_MS doubled from 2000 to 4000) so viewers have
 *      time to read + absorb the geographic moment without the card blinking
 *      off too quickly.
 *   2. Add a persistent upper-center phase banner showing the current stage
 *      of the journey (Birmingham / Crossing Red Mountain / Lone Pine Gap
 *      Cut / Descending to Shades Valley / Edgewood Lake). This removes the
 *      failure mode Tom hit: missing a card fade and then having no visual
 *      cue where he is.
 *
 * Cards:
 *   - navState transitions to `boarding` -> "Birmingham, 1925"
 *   - streetcarRiding progress crosses ~0.5 -> "Lone Pine Gap"
 *   - navState transitions to `arrivalCinematic` -> "Edgewood Lake"
 *
 * Each card fades in over FADE_MS, holds HOLD_MS, fades out over FADE_MS.
 *
 * Phase banner: always visible once navState !== 'idle', updated from the
 * combination of navState + progress. Pointer-events:none so it never
 * blocks clicks. Period-quiet chrome (cream text, espresso shadow, warm-
 * ochre accent rule).
 *
 * Implementation notes:
 *   - Pure React. No R3F hooks. Lives in UIShell as a DOM overlay.
 *   - Uses setTimeout for hold+fade-out timing; cleanup on unmount cancels.
 *   - Subscribes to useStreetcarStore.progress for the midway trigger +
 *     phase-banner text.
 *   - One-shot-per-ride: each card fires at most once per session to avoid
 *     flicker during the streetcarRiding frame where progress oscillates
 *     around 0.5. REBOARD resets the midway guard via the navState-entry
 *     branch below.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavStore, type NavStateValue } from '../store/useNavStore';
import { useStreetcarStore } from '../store/useStreetcarStore';
import { typography, periodChrome } from './PeriodChromeStyles';

const FADE_MS = 800;
const HOLD_MS = 4000; // doubled from Phase 8 v1 per Tom's live-site feedback

/**
 * Visible phase of a title card.
 *   - 'in'    : fade-in (opacity 0 -> 1 over FADE_MS)
 *   - 'hold'  : fully visible for HOLD_MS
 *   - 'out'   : fade-out (opacity 1 -> 0 over FADE_MS)
 *   - null    : not rendered
 */
type CardPhase = 'in' | 'hold' | 'out' | null;

interface ActiveCard {
  text: string;
  phase: CardPhase;
}

/**
 * Derive the upper-center phase-banner text from the current state.
 *
 * Banner text is progress-sensitive during streetcarRiding so the viewer
 * reads an evolving caption rather than a single static label for the
 * whole 90-second ride:
 *
 *   idle                   → '' (hidden — BoardingOverlay owns this moment)
 *   boarding               → 'BIRMINGHAM · 1925'
 *   streetcarRiding t<0.35 → 'CLIMBING RED MOUNTAIN'
 *   streetcarRiding 0.35≤t<0.65 → 'LONE PINE GAP CUT'
 *   streetcarRiding t≥0.65 → 'DESCENDING INTO SHADES VALLEY'
 *   arrivalCinematic       → 'EDGEWOOD LAKE · HOMEWOOD, ALABAMA'
 *   freeWalk / guidedTour  → 'EDGEWOOD LAKE · HOMEWOOD, ALABAMA'
 *   photoCompare           → 'THEN & NOW'
 */
function derivePhaseLabel(
  navState: NavStateValue,
  progress: number,
): string {
  switch (navState) {
    case 'idle':
      return '';
    case 'boarding':
      return 'BIRMINGHAM  ·  1925';
    case 'streetcarRiding':
      if (progress < 0.35) return 'CLIMBING RED MOUNTAIN';
      if (progress < 0.65) return 'LONE PINE GAP CUT';
      return 'DESCENDING INTO SHADES VALLEY';
    case 'arrivalCinematic':
      return 'EDGEWOOD LAKE  ·  HOMEWOOD, ALABAMA';
    case 'freeWalk':
    case 'guidedTour':
      return 'EDGEWOOD LAKE  ·  HOMEWOOD, ALABAMA';
    case 'photoCompare':
      return 'THEN  &  NOW';
    default:
      return '';
  }
}

export function TitleCards() {
  const navState = useNavStore((s) => s.navState);
  const progress = useStreetcarStore((s) => s.progress);
  const [active, setActive] = useState<ActiveCard | null>(null);
  // Guards — each card only fires once per session. Resetting the ride
  // (REBOARD back to streetcarRiding) clears the midway guard so the
  // "Lone Pine Gap" card reappears if the user rides twice.
  const firedRef = useRef({
    boarding: false,
    lonePine: false,
    arrival: false,
  });
  // Track the last nav state we saw so we can detect the streetcarRiding
  // re-entry and reset the midway guard without flashing on every tick.
  const lastNavRef = useRef<NavStateValue>(navState);
  // Timer handles for cleanup
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  function clearTimers() {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }

  function showCard(text: string) {
    clearTimers();
    setActive({ text, phase: 'in' });
    // After FADE_MS, go to hold; after FADE_MS + HOLD_MS, go to out;
    // after FADE_MS * 2 + HOLD_MS, clear.
    timersRef.current.push(
      setTimeout(() => {
        setActive((prev) => (prev ? { ...prev, phase: 'hold' } : prev));
      }, FADE_MS),
    );
    timersRef.current.push(
      setTimeout(() => {
        setActive((prev) => (prev ? { ...prev, phase: 'out' } : prev));
      }, FADE_MS + HOLD_MS),
    );
    timersRef.current.push(
      setTimeout(() => {
        setActive(null);
      }, FADE_MS * 2 + HOLD_MS),
    );
  }

  // Nav-state-driven cards (boarding + arrival). Edge-triggered on state
  // entry via the navState dependency.
  useEffect(() => {
    if (navState === 'boarding' && !firedRef.current.boarding) {
      firedRef.current.boarding = true;
      showCard('Birmingham, 1925');
    }
    if (navState === 'arrivalCinematic' && !firedRef.current.arrival) {
      firedRef.current.arrival = true;
      showCard('Edgewood Lake');
    }
    // Reset the midway guard when entering streetcarRiding from any other
    // state (idle->boarding->streetcarRiding on first ride; freeWalk->
    // streetcarRiding on REBOARD).
    if (
      navState === 'streetcarRiding' &&
      lastNavRef.current !== 'streetcarRiding'
    ) {
      firedRef.current.lonePine = false;
    }
    lastNavRef.current = navState;
  }, [navState]);

  // Progress-driven midway card. Only fires while the machine is in
  // streetcarRiding AND progress has crossed the midpoint. The 0.45-0.55
  // window provides a little slack so a jittery frame right around 0.5
  // doesn't miss the trigger.
  useEffect(() => {
    if (
      navState === 'streetcarRiding' &&
      progress >= 0.45 &&
      progress <= 0.6 &&
      !firedRef.current.lonePine
    ) {
      firedRef.current.lonePine = true;
      showCard('Lone Pine Gap');
    }
  }, [navState, progress]);

  // Cleanup timers on unmount
  useEffect(() => {
    return clearTimers;
  }, []);

  const phaseLabel = derivePhaseLabel(navState, progress);
  const showPhaseBanner = navState !== 'idle' && phaseLabel.length > 0;
  const showLocationLabel = navState !== 'idle';

  return (
    <>
      {active && (
        <div
          aria-live="polite"
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 160,
            opacity: active.phase === 'hold' ? 1 : active.phase === 'in' ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease-in-out`,
          }}
          data-phase={active.phase}
          data-testid="title-card"
        >
          <TitleCardInner text={active.text} phase={active.phase} />
        </div>
      )}
      {/* Persistent phase banner — upper-center, pointer-events:none. Small
          serif caps so it reads as a subtle chrome anchor rather than a
          billboard. This is the "you always know where you are" element
          Tom's live-site feedback called out as missing. */}
      {showPhaseBanner && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            top: '18px',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 25,
            fontFamily: typography.serif,
            fontSize: '13px',
            fontStyle: 'italic',
            color: periodChrome.cream,
            textShadow: '0 1px 3px rgba(0, 0, 0, 0.7)',
            letterSpacing: '0.18em',
            opacity: 0.9,
            userSelect: 'none',
            padding: '6px 22px',
            // Subtle scrim so the text reads over any scene color while
            // staying period-quiet (cream band inside warm-ochre 1px rule).
            backgroundColor: 'rgba(59, 47, 47, 0.28)',
            borderTop: `1px solid ${periodChrome.ochre}`,
            borderBottom: `1px solid ${periodChrome.ochre}`,
            whiteSpace: 'nowrap',
          }}
          data-testid="phase-banner"
        >
          {phaseLabel}
        </div>
      )}
      {showLocationLabel && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            bottom: '16px',
            left: '16px',
            pointerEvents: 'none',
            zIndex: 20,
            fontFamily: typography.serif,
            fontSize: '11px',
            letterSpacing: '0.04em',
            color: periodChrome.cream,
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.45)',
            opacity: 0.75,
            fontStyle: 'italic',
            userSelect: 'none',
          }}
          data-testid="location-label"
        >
          Edgewood Lake, Homewood, Alabama &middot; ~1925
        </div>
      )}
    </>
  );
}

/**
 * Inner card wrapper. Starts at opacity 0 on mount and transitions to 1
 * over FADE_MS for the fade-in. When the outer phase flips to 'out', the
 * outer container's opacity transition handles the fade-out.
 */
function TitleCardInner({ text, phase }: { text: string; phase: CardPhase }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const innerOpacity = phase === 'out' ? 1 : mounted ? 1 : 0;
  return (
    <div
      style={{
        opacity: innerOpacity,
        transition: `opacity ${FADE_MS}ms ease-in-out`,
        backgroundColor: 'rgba(59, 47, 47, 0.78)',
        padding: '64px 96px',
        borderTop: `1px solid ${periodChrome.ochre}`,
        borderBottom: `1px solid ${periodChrome.ochre}`,
        minWidth: '280px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontFamily: typography.serif,
          fontSize: '44px',
          letterSpacing: '0.08em',
          color: periodChrome.cream,
          fontStyle: 'italic',
          fontWeight: 400,
          lineHeight: 1.2,
          userSelect: 'none',
        }}
      >
        {text}
      </div>
    </div>
  );
}
